/* eslint-disable no-shadow */
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { isBoolean } from "lodash";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button, Flex, Grid, GridDragArea } from "uikit";
import { SortType } from "rsuite-table";

import { Car, Executor } from "../../../services";
import { useTypedDispatch, useTypedSelector } from "../../../redux/store";
import getExecutorsByCarCallsign from "../../../redux/services/Order/getExecutorsByCarCallsign";
import useExecutorsActions from "../../../hooks/useExecutorsActions";
import { useCheckFlagAccessControl } from "../../../hooks";
import { SVGgeneral } from "../../../utils/generalSprite";
import { StyledRow, StyledColumn, AccessGuard } from "../../common";
import SearchBarComponent from "../../SearchBar";
import { useTableOptions } from "../../LightTable";

import { TabAccessNames, TabKeys } from "./constants/access";
import { EXECUTORS_TABLE_PAGE_SIZE } from "./constants";
import useExecutorWidgetModels from "./hooks/useExecutorWidgetModels";
import ExecutorModal from "./components/ExecutorModal";
import TableFooter from "./components/TableFooter";
import CallExecutorButton from "./components/CallExexcutorButton";
import { Filter, Header, Table } from "./components";

const SearchBar = styled(SearchBarComponent)`
	width: 280px !important;
`;

const DragButtonRoot = styled(Flex)`
	flex-basis: 18px;

	cursor: grab;
`;

function DragButton() {
	const item = useContext(Grid.Item.ItemContext);

	return item?.indexes.length === 1 ? (
		<GridDragArea.GridDragArea indexes={item.indexes}>
			<DragButtonRoot align="center" justify="start">
				<SVGgeneral id="drag_icon" />
			</DragButtonRoot>
		</GridDragArea.GridDragArea>
	) : null;
}

function ExecutorsWidget() {
	const dispatch = useTypedDispatch();
	const { t } = useTranslation();
	const { setIsSortMap } = useExecutorsActions();
	const checkFlagAccessControl = useCheckFlagAccessControl(TabAccessNames);
	const { sort, editor, onChange, getPaginatedData, setLimit } =
		useTableOptions({
			itemLimit: EXECUTORS_TABLE_PAGE_SIZE,
			initialLimit: EXECUTORS_TABLE_PAGE_SIZE,
		});

	const globalLang = useTypedSelector((state) => state.session.language);
	const { criteria } = useTypedSelector(
		(state) => state.orders.executors.filter,
	);
	const executorsByCarCallsign = useTypedSelector(
		(state) => state.ordersPageReducer.executorsByCarCallsign,
	);

	const [selected, setSelected] = useState<Executor.Model | null>();
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [alias, setAlias] = useState<string>("");
	const [rerenderSelected, setRerenderSelected] = useState<boolean>(true);
	const [showExecutorModal, setShowExecutorModal] = useState<boolean>(false);
	const [isEdit, setIsEdit] = useState<boolean>(false);
	const [query, setQuery] = useState<string>("");
	const [prevQuery, setPrevQuery] = useState<string>("");
	const [foundedExecutorId, setFoundedExecutorId] = useState<number | null>(
		-1,
	);

	const { executorsSubscribe, filteredExecutors } = useExecutorWidgetModels(
		query,
		sort,
	);

	const tableExecutors = useMemo(
		() => getPaginatedData(filteredExecutors),
		[filteredExecutors, getPaginatedData],
	);

	useEffect(() => {
		setLimit(EXECUTORS_TABLE_PAGE_SIZE);
	}, [
		query,
		criteria.online,
		criteria.workingStatus,
		criteria.statuses,
		criteria.taxiServiceIds,
		criteria.companyIds,
		criteria.executorGroupIds,
		criteria.fleetIds,
		sort.dataKey,
		sort.sortType,
		setLimit,
	]);

	const closeExecutorModal = () => {
		setShowExecutorModal(false);
		setSelected(null);
		setAlias("");
		setIsEdit(false);
	};

	useEffect(() => {
		setIsSortMap(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onSave = async (
		id,
		value,
		executor: Executor.Model,
		isWorking: boolean,
		selectedCar: Car.Model | null,
	) => {
		if (executor.isWorking !== isWorking) {
			const getCar = (exe: Executor.Model, carId?: number) => {
				if (carId) return carId ?? null;

				const car = exe.cars?.find((car) => car?.active);

				return car?.id ?? null;
			};

			const carId = getCar(executor, selectedCar?.id);

			await Executor.updateWorkingStatus(
				{ id, workingStatus: isWorking },
				carId,
			);

			setIsSortMap(true);
		}

		if (value?.reason) {
			const requestData = {
				id,
				reason: value.reason,
				until: value.until,
			};

			if (!value.until) {
				delete requestData.until;
			}
			if (executor.status !== "closed") {
				await Executor.close(requestData);

				setIsSortMap(true);
			}
			closeExecutorModal();
		} else {
			const requestData =
				typeof value === "object"
					? { id, ...value }
					: { id, status: value };
			if (
				requestData?.status ||
				isBoolean(requestData?.closedBy) ||
				isBoolean(value?.debt)
			) {
				if (
					executor.status !== value?.status ||
					(executor.debt !== value?.debt && value?.debt === false) ||
					(executor.closedBy !== value?.closedBy &&
						value?.closedBy === false)
				) {
					await Executor.update(requestData, "status");

					setIsSortMap(true);
				}
			}
			closeExecutorModal();
		}

		setSelected(null);
		setIsEdit(false);
	};

	const tableRowClassName = useCallback(
		(rowData: Executor.Model) => {
			if (rowData && rowData?.id === selectedId) {
				if (foundedExecutorId === rowData?.id) return "founded";
				if (!rowData.active) return "founded not-active";
				return "founded";
			}

			if (rowData && foundedExecutorId === rowData?.id) {
				if (!rowData.active) return "founded not-active";
				return "founded";
			}

			if (rowData && !rowData.active) return "not-active";

			return "";
		},
		[foundedExecutorId, selectedId],
	);

	const onRowSelect = (rowData: Executor.Model) => {
		if (rowData?.id === selectedId) {
			setSelectedId(null);
		} else {
			setSelectedId(rowData.id);
		}
	};

	const getExecutor = useCallback(
		async (executor: Executor.Model | undefined) => {
			setSelected(executor);
		},
		[],
	);

	const onDoubleClick = async (rowData: Executor.Model) => {
		setSelectedId(rowData.id);
		setShowExecutorModal(true);

		const executor = executorsSubscribe.find(
			(executor) => executor.id === rowData.id,
		);

		await getExecutor(executor);
		setIsEdit(true);
	};

	const onGetExecutor = useCallback(
		(executor: Executor.Model | undefined) => {
			getExecutor(executor);
		},
		[getExecutor],
	);

	const onClickAddExecutor = useCallback(() => {
		setSelected(null);
		setAlias("");
		setShowExecutorModal(true);
		setSelectedId(null);
		setIsEdit(false);
	}, []);

	const onClickEditExecutor = useCallback(async () => {
		setShowExecutorModal(true);
		setIsEdit(true);

		const executor = executorsSubscribe.find(
			(executor) => executor.id === selectedId,
		);

		await getExecutor(executor);
	}, [executorsSubscribe, getExecutor, selectedId]);

	useEffect(() => {
		if (query[0] === "*") {
			const queryWithoutStar = query.slice(1);
			executorsByCarCallsign.forEach((executor) => {
				if (
					executor?.executorToCrews[0]?.crew?.car?.callSign ===
					queryWithoutStar
				) {
					setFoundedExecutorId(executor.id);
					setPrevQuery(queryWithoutStar);
				}
				if (prevQuery !== queryWithoutStar) {
					setFoundedExecutorId(null);
				}
			});
		} else {
			executorsSubscribe?.forEach((executor) => {
				if (executor.alias === query) {
					setFoundedExecutorId(executor.id);
					setPrevQuery(query);
				}
				if (prevQuery !== query) {
					setFoundedExecutorId(null);
				}
			});
		}
	}, [query, prevQuery, executorsSubscribe, executorsByCarCallsign]);

	useEffect(() => {
		if (showExecutorModal && (alias || selectedId)) {
			setIsEdit(true);
		}

		if (!showExecutorModal && isEdit) {
			const executor = executorsSubscribe.find(
				(executor) => executor.id === selectedId,
			);
			getExecutor(executor);
		}
	}, [
		selectedId,
		rerenderSelected,
		alias,
		isEdit,
		showExecutorModal,
		executorsSubscribe,
		getExecutor,
	]);

	useEffect(() => {
		if (query[0] === "*") {
			const queryWithoutStar = query.slice(1);
			dispatch(getExecutorsByCarCallsign(queryWithoutStar));
			setIsSortMap(true);
		}
	}, [dispatch, query, setIsSortMap]);

	const getExecutorById = useCallback(
		(id: number | null) => {
			if (!id) return null;
			const exist = executorsSubscribe?.find((item) => item.id === id);
			return exist;
		},
		[executorsSubscribe],
	);

	const isDoubleBecauseOfAccess = useMemo(() => {
		const isAccessViewInModalOrder =
			checkFlagAccessControl[TabKeys.CHANGE_PERFORMERS_STATUS_SELECTED];

		return isAccessViewInModalOrder;
	}, [checkFlagAccessControl]);

	const nameColuumnsGroupFirst: ExecutorsWidget.TabMain = useMemo(
		() => ({
			reduxLocal: "OrdersPage2.orderWidgetExecutors",
			name: "orderWidgetExecutors",
		}),
		[],
	);

	return (
		<>
			{showExecutorModal && (
				<ExecutorModal
					onClose={closeExecutorModal}
					onSave={onSave}
					// selected={selected}
					_selected={selected}
					selectedId={selectedId}
					alias={alias}
					isEdit={isEdit}
					setAlias={setAlias}
					setSelectedId={setSelectedId}
					setSelected={onGetExecutor}
					// executors={executorsSubscribe}
					setRerenderSelected={setRerenderSelected}
				/>
			)}
			<StyledColumn w="100%" h="100%">
				<Header maxedWidth justify="space-between">
					<StyledRow gap="4px">
						<AccessGuard
							accessName={
								TabAccessNames[TabKeys.CHANGE_PERFORMERS_STATUS]
							}
						>
							<StyledRow gap="4px" alignItems="center">
								<DragButton />
								<Button.Button
									variant="secondary"
									icon={<SVGgeneral id="addExecutor" />}
									onClick={onClickAddExecutor}
								/>
							</StyledRow>
						</AccessGuard>
						<AccessGuard
							accessName={
								TabAccessNames[
									TabKeys.CHANGE_PERFORMERS_STATUS_SELECTED
								]
							}
						>
							<StyledRow gap="4px" alignItems="center">
								<DragButton />
								<Button.Button
									variant="secondary"
									icon={<SVGgeneral id="pencil" />}
									onClick={onClickEditExecutor}
									disabled={!selectedId}
								/>
							</StyledRow>
						</AccessGuard>
						<AccessGuard
							accessName={
								TabAccessNames[TabKeys.CALL_ORDER_EXECUTOR]
							}
						>
							<CallExecutorButton
								executor={getExecutorById(selectedId)}
							/>
						</AccessGuard>
					</StyledRow>
					<StyledRow
						/* sizes="auto*" */ gap="10px"
						alignItems="center"
					>
						<AccessGuard
							accessName={TabAccessNames[TabKeys.SEARCH]}
						>
							<SearchBar
								value={query}
								onChange={(value) => {
									setQuery(value);
								}}
								placeholder={
									t("orderPageWidgets.executors.find") ?? ""
								}
							/>
						</AccessGuard>
						<AccessGuard
							accessName={TabAccessNames[TabKeys.FILTER]}
						>
							<Filter language={globalLang} />
						</AccessGuard>
					</StyledRow>
				</Header>

				<Table
					onRowSelect={onRowSelect}
					onRowDoubleClick={(rowData) => {
						if (isDoubleBecauseOfAccess) onDoubleClick(rowData);
					}}
					tableRowClassName={tableRowClassName}
					data={tableExecutors}
					loading={false}
					nameColuumnsGroup={nameColuumnsGroupFirst}
					editorTable={editor}
					onChangeTable={onChange}
					tableFooter={
						<AccessGuard
							accessName={
								TabAccessNames[TabKeys.LOWERS_STATISTICS_TABLE]
							}
						>
							<TableFooter executors={filteredExecutors} />
						</AccessGuard>
					}
				/>
			</StyledColumn>
		</>
	);
}

export declare namespace ModelTable {
	interface Sort {
		column: string | undefined;
		type: SortType | undefined;
	}

	interface RequestValue {
		reason?: string;
	}
}

export declare namespace ExecutorsWidget {
	interface TabMain {
		reduxLocal: "OrdersPage2.orderWidgetExecutors";
		name: "orderWidgetExecutors";
	}
}

export default ExecutorsWidget;
